import { classificationKey, type ColumnClassificationMap } from '../columnClassificationStore';
import { logDataModels } from '../dataModelsLogger';
import type { DataModel, ModelTable, Space } from '../dataModelsTypes';
import { columnsForTable } from '../dataModelsUtils';
import type {
  Classification,
  PolicyAsset,
  PolicyColumn,
  PolicyModel,
  PolicyScope,
  PolicyVault,
  SyntheticUser,
} from './types';

function col(name: string, type: string, classification?: Classification, tags: string[] = [], nullable = false): PolicyColumn {
  return { name, type, classification, tags, nullable };
}

function descendants(managerId: string, users: SyntheticUser[]): string[] {
  const children = users.filter((user) => user.managerId === managerId);
  return children.flatMap((child) => [child.employeeId, ...descendants(child.employeeId, users)]);
}

export const POLICY_USERS: SyntheticUser[] = [
  { id: 'usr_alice', name: 'Alice', email: 'alice@selfic.ai', role: 'Admin', roles: ['admin'], groups: ['IT'], department: 'IT', region: 'US', tenantId: 'tenant_us', employeeId: 'E001', clearance: 'high', channel: 'portal' },
  { id: 'usr_bob', name: 'Bob', email: 'bob@selfic.ai', role: 'Manager', roles: ['manager'], groups: ['Sales'], department: 'Sales', region: 'US', tenantId: 'tenant_us', employeeId: 'E002', clearance: 'medium', channel: 'portal' },
  { id: 'usr_charlie', name: 'Charlie', email: 'charlie@selfic.ai', role: 'Analyst', roles: ['analyst'], groups: ['Sales'], department: 'Sales', region: 'US', tenantId: 'tenant_us', employeeId: 'E003', managerId: 'E002', clearance: 'low', channel: 'mcp' },
  { id: 'usr_david', name: 'David', email: 'david@selfic.ai', role: 'Engineer', roles: ['engineer'], groups: ['Engineering'], department: 'Engineering', region: 'EU', tenantId: 'tenant_eu', employeeId: 'E004', managerId: 'E003', clearance: 'medium', channel: 'mcp' },
  { id: 'usr_emma', name: 'Emma', email: 'emma@selfic.ai', role: 'HR Manager', roles: ['hr_manager', 'manager'], groups: ['HR'], department: 'HR', region: 'US', tenantId: 'tenant_us', employeeId: 'E005', clearance: 'high', channel: 'portal' },
  { id: 'usr_frank', name: 'Frank', email: 'frank@external.ai', role: 'External Consumer', roles: ['external'], groups: ['External', 'Contractors'], department: 'External', region: 'EU', tenantId: 'tenant_eu', employeeId: 'E006', clearance: 'none', channel: 'api' },
];

export const POLICY_GROUPS = ['Finance', 'HR', 'Sales', 'Marketing', 'Engineering', 'Contractors', 'External', 'IT'];
export const POLICY_ROLES = ['admin', 'manager', 'analyst', 'engineer', 'hr_manager', 'external'];

export function descendantEmployeeIds(employeeId: string): string[] {
  return descendants(employeeId, POLICY_USERS);
}

export function userContext(user: SyntheticUser): Record<string, unknown> {
  return {
    'user.id': user.id,
    'user.email': user.email,
    'user.roles': user.roles,
    'user.groups': user.groups,
    'user.department': user.department,
    'user.region': user.region,
    'user.tenant_id': user.tenantId,
    'user.employee_id': user.employeeId,
    'user.manager_id': user.managerId ?? '',
    'user.attributes.clearance': user.clearance,
    'user.descendant_employee_ids': descendantEmployeeIds(user.employeeId),
  };
}

const NAMED_ASSETS: PolicyAsset[] = [
  {
    id: 'tbl-customer-profile',
    name: 'customer_profile',
    vaultId: 'spc-customer',
    modelIds: ['mdl-customer-360'],
    connectionType: 'snowflake',
    classification: 'PII',
    tags: ['customer', 'pii'],
    columns: [
      col('customer_id', 'BIGINT', 'Internal', ['identifier']),
      col('full_name', 'VARCHAR', 'PII', ['pii', 'name']),
      col('email', 'VARCHAR', 'PII', ['pii', 'email']),
      col('phone', 'VARCHAR', 'PII', ['pii', 'phone']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('tenant_id', 'VARCHAR', 'Internal', ['tenant']),
      col('account_manager_id', 'VARCHAR', 'Internal', ['manager']),
      col('created_at', 'TIMESTAMP', 'Internal', ['audit']),
    ],
    rows: [
      { customer_id: 1001, full_name: 'Nora Blake', email: 'nora.blake@example.com', phone: '+1-415-555-0101', region: 'US', tenant_id: 'tenant_us', account_manager_id: 'E002', created_at: '2024-01-12T10:00:00Z' },
      { customer_id: 1002, full_name: 'Luis Romero', email: 'luis.romero@example.com', phone: '+1-212-555-0144', region: 'US', tenant_id: 'tenant_us', account_manager_id: 'E003', created_at: '2024-03-02T11:20:00Z' },
      { customer_id: 1003, full_name: 'Elena Vogt', email: 'elena.vogt@example.com', phone: '+49-30-555-0188', region: 'EU', tenant_id: 'tenant_eu', account_manager_id: 'E004', created_at: '2024-04-18T08:15:00Z' },
      { customer_id: 1004, full_name: 'Priya Shah', email: 'priya.shah@example.com', phone: '+1-650-555-0199', region: 'US', tenant_id: 'tenant_us', account_manager_id: 'E002', created_at: '2024-05-01T14:40:00Z' },
      { customer_id: 1005, full_name: 'Ken Ito', email: 'ken.ito@example.com', phone: '+81-3-555-0112', region: 'APAC', tenant_id: 'tenant_apac', account_manager_id: 'E003', created_at: '2024-06-21T09:05:00Z' },
      { customer_id: 1006, full_name: 'Maya Cole', email: 'maya.cole@example.com', phone: '+1-617-555-0166', region: 'US', tenant_id: 'tenant_us', account_manager_id: 'E002', created_at: '2024-07-08T16:22:00Z' },
      { customer_id: 1007, full_name: 'Omar Reid', email: 'omar.reid@example.com', phone: '+44-20-555-0177', region: 'EU', tenant_id: 'tenant_eu', account_manager_id: 'E004', created_at: '2024-08-14T12:00:00Z' },
      { customer_id: 1008, full_name: 'Ivy Tran', email: 'ivy.tran@example.com', phone: '+1-206-555-0133', region: 'US', tenant_id: 'tenant_us', account_manager_id: 'E003', created_at: '2024-09-03T07:48:00Z' },
    ],
  },
  {
    id: 'tbl-customer-orders',
    name: 'customer_orders',
    vaultId: 'spc-customer',
    modelIds: ['mdl-customer-360'],
    connectionType: 'postgresql',
    classification: 'Internal',
    tags: ['orders', 'customer'],
    columns: [
      col('order_id', 'BIGINT', 'Internal', ['identifier']),
      col('customer_id', 'BIGINT', 'Internal', ['customer']),
      col('order_amount', 'DECIMAL', 'Confidential', ['financial']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('tenant_id', 'VARCHAR', 'Internal', ['tenant']),
      col('order_date', 'DATE', 'Internal', ['orderdate']),
      col('account_manager_id', 'VARCHAR', 'Internal', ['manager']),
    ],
    rows: [
      { order_id: 9001, customer_id: 1001, order_amount: 125.5, region: 'US', tenant_id: 'tenant_us', order_date: '2026-09-01', account_manager_id: 'E002' },
      { order_id: 9002, customer_id: 1002, order_amount: 89.99, region: 'US', tenant_id: 'tenant_us', order_date: '2026-09-04', account_manager_id: 'E003' },
      { order_id: 9003, customer_id: 1003, order_amount: 299, region: 'EU', tenant_id: 'tenant_eu', order_date: '2026-09-08', account_manager_id: 'E004' },
      { order_id: 9004, customer_id: 1004, order_amount: 45, region: 'US', tenant_id: 'tenant_us', order_date: '2026-09-12', account_manager_id: 'E002' },
      { order_id: 9005, customer_id: 1005, order_amount: 510.25, region: 'APAC', tenant_id: 'tenant_apac', order_date: '2026-09-16', account_manager_id: 'E003' },
      { order_id: 9006, customer_id: 1006, order_amount: 72.1, region: 'US', tenant_id: 'tenant_us', order_date: '2026-09-20', account_manager_id: 'E002' },
      { order_id: 9007, customer_id: 1007, order_amount: 188, region: 'EU', tenant_id: 'tenant_eu', order_date: '2026-09-24', account_manager_id: 'E004' },
      { order_id: 9008, customer_id: 1008, order_amount: 64.75, region: 'US', tenant_id: 'tenant_us', order_date: '2026-09-28', account_manager_id: 'E003' },
    ],
  },
  {
    id: 'tbl-customer-contacts',
    name: 'customer_contacts',
    vaultId: 'spc-customer',
    modelIds: ['mdl-customer-360', 'mdl-marketing-analytics'],
    connectionType: 'mongodb',
    classification: 'PII',
    tags: ['contacts', 'pii'],
    columns: [
      col('contact_id', 'VARCHAR', 'Internal', ['identifier']),
      col('customer_id', 'BIGINT', 'Internal', ['customer']),
      col('email', 'VARCHAR', 'PII', ['pii', 'email']),
      col('phone', 'VARCHAR', 'PII', ['pii', 'phone']),
      col('channel', 'VARCHAR', 'Internal', ['channel']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('tenant_id', 'VARCHAR', 'Internal', ['tenant']),
    ],
    rows: [
      { contact_id: 'C-1', customer_id: 1001, email: 'nora.blake@example.com', phone: '+1-415-555-0101', channel: 'email', region: 'US', tenant_id: 'tenant_us' },
      { contact_id: 'C-2', customer_id: 1002, email: 'luis.romero@example.com', phone: '+1-212-555-0144', channel: 'sms', region: 'US', tenant_id: 'tenant_us' },
      { contact_id: 'C-3', customer_id: 1003, email: 'elena.vogt@example.com', phone: '+49-30-555-0188', channel: 'email', region: 'EU', tenant_id: 'tenant_eu' },
      { contact_id: 'C-4', customer_id: 1004, email: 'priya.shah@example.com', phone: '+1-650-555-0199', channel: 'phone', region: 'US', tenant_id: 'tenant_us' },
      { contact_id: 'C-5', customer_id: 1007, email: 'omar.reid@example.com', phone: '+44-20-555-0177', channel: 'email', region: 'EU', tenant_id: 'tenant_eu' },
      { contact_id: 'C-6', customer_id: 1008, email: 'ivy.tran@example.com', phone: '+1-206-555-0133', channel: 'sms', region: 'US', tenant_id: 'tenant_us' },
    ],
  },
  {
    id: 'tbl-customer-transactions',
    name: 'customer_transactions',
    vaultId: 'spc-customer',
    modelIds: ['mdl-customer-360'],
    connectionType: 'bigquery',
    classification: 'Confidential',
    tags: ['transactions', 'finance'],
    columns: [
      col('transaction_id', 'VARCHAR', 'Internal', ['identifier']),
      col('customer_id', 'BIGINT', 'Internal', ['customer']),
      col('order_id', 'BIGINT', 'Internal', ['order']),
      col('amount', 'DECIMAL', 'Confidential', ['financial']),
      col('currency', 'VARCHAR', 'Internal', ['financial']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('tenant_id', 'VARCHAR', 'Internal', ['tenant']),
      col('card_last4', 'VARCHAR', 'PCI', ['pci', 'payment']),
    ],
    rows: [
      { transaction_id: 'TXN_0001', customer_id: 1001, order_id: 9001, amount: 125.5, currency: 'USD', region: 'US', tenant_id: 'tenant_us', card_last4: '4242' },
      { transaction_id: 'TXN_0002', customer_id: 1002, order_id: 9002, amount: 89.99, currency: 'USD', region: 'US', tenant_id: 'tenant_us', card_last4: '1111' },
      { transaction_id: 'TXN_0003', customer_id: 1003, order_id: 9003, amount: 299, currency: 'EUR', region: 'EU', tenant_id: 'tenant_eu', card_last4: '0005' },
      { transaction_id: 'TXN_0004', customer_id: 1004, order_id: 9004, amount: 45, currency: 'USD', region: 'US', tenant_id: 'tenant_us', card_last4: '1881' },
      { transaction_id: 'TXN_0005', customer_id: 1007, order_id: 9007, amount: 188, currency: 'EUR', region: 'EU', tenant_id: 'tenant_eu', card_last4: '5555' },
    ],
  },
  {
    id: 'tbl-marketing-preferences',
    name: 'marketing_preferences',
    vaultId: 'spc-customer',
    modelIds: ['mdl-marketing-analytics'],
    connectionType: 'snowflake',
    classification: 'Internal',
    tags: ['marketing', 'consent'],
    columns: [
      col('preference_id', 'VARCHAR', 'Internal', ['identifier']),
      col('customer_id', 'BIGINT', 'Internal', ['customer']),
      col('email', 'VARCHAR', 'PII', ['pii', 'email']),
      col('channel', 'VARCHAR', 'Internal', ['channel']),
      col('opt_in', 'BOOLEAN', 'Internal', ['consent']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('tenant_id', 'VARCHAR', 'Internal', ['tenant']),
    ],
    rows: [
      { preference_id: 'P-1', customer_id: 1001, email: 'nora.blake@example.com', channel: 'email', opt_in: true, region: 'US', tenant_id: 'tenant_us' },
      { preference_id: 'P-2', customer_id: 1003, email: 'elena.vogt@example.com', channel: 'sms', opt_in: false, region: 'EU', tenant_id: 'tenant_eu' },
      { preference_id: 'P-3', customer_id: 1004, email: 'priya.shah@example.com', channel: 'email', opt_in: true, region: 'US', tenant_id: 'tenant_us' },
      { preference_id: 'P-4', customer_id: 1007, email: 'omar.reid@example.com', channel: 'email', opt_in: true, region: 'EU', tenant_id: 'tenant_eu' },
    ],
  },
  {
    id: 'tbl-employees',
    name: 'employees',
    vaultId: 'spc-hr',
    modelIds: ['mdl-workforce-roster'],
    connectionType: 'postgresql',
    classification: 'PII',
    tags: ['hr', 'pii'],
    columns: [
      col('employee_id', 'VARCHAR', 'Internal', ['identifier']),
      col('employee_name', 'VARCHAR', 'PII', ['pii', 'name']),
      col('email', 'VARCHAR', 'PII', ['pii', 'email']),
      col('manager_id', 'VARCHAR', 'Internal', ['manager'], true),
      col('department', 'VARCHAR', 'Internal', ['department']),
      col('region', 'VARCHAR', 'Internal', ['region']),
    ],
    rows: [
      { employee_id: 'E001', employee_name: 'Alice', email: 'alice@selfic.ai', manager_id: null, department: 'IT', region: 'US' },
      { employee_id: 'E002', employee_name: 'Bob', email: 'bob@selfic.ai', manager_id: 'E001', department: 'Sales', region: 'US' },
      { employee_id: 'E003', employee_name: 'Charlie', email: 'charlie@selfic.ai', manager_id: 'E002', department: 'Sales', region: 'US' },
      { employee_id: 'E004', employee_name: 'David', email: 'david@selfic.ai', manager_id: 'E003', department: 'Engineering', region: 'EU' },
      { employee_id: 'E005', employee_name: 'Emma', email: 'emma@selfic.ai', manager_id: 'E001', department: 'HR', region: 'US' },
      { employee_id: 'E006', employee_name: 'Frank', email: 'frank@external.ai', manager_id: null, department: 'External', region: 'EU' },
    ],
  },
  {
    id: 'tbl-employee-hierarchy',
    name: 'employee_hierarchy',
    vaultId: 'spc-hr',
    modelIds: ['mdl-workforce-roster'],
    connectionType: 'postgresql',
    classification: 'Internal',
    tags: ['hr', 'hierarchy'],
    columns: [
      col('manager_id', 'VARCHAR', 'Internal', ['manager']),
      col('employee_id', 'VARCHAR', 'Internal', ['identifier']),
    ],
    rows: [
      { manager_id: 'E001', employee_id: 'E002' },
      { manager_id: 'E002', employee_id: 'E003' },
      { manager_id: 'E003', employee_id: 'E004' },
      { manager_id: 'E001', employee_id: 'E005' },
    ],
  },
  {
    id: 'tbl-departments',
    name: 'departments',
    vaultId: 'spc-hr',
    modelIds: ['mdl-workforce-roster'],
    connectionType: 'postgresql',
    classification: 'Internal',
    tags: ['hr'],
    columns: [
      col('department_id', 'VARCHAR', 'Internal', ['identifier']),
      col('department', 'VARCHAR', 'Internal', ['department']),
      col('region', 'VARCHAR', 'Internal', ['region']),
    ],
    rows: [
      { department_id: 'D-IT', department: 'IT', region: 'US' },
      { department_id: 'D-SALES', department: 'Sales', region: 'US' },
      { department_id: 'D-ENG', department: 'Engineering', region: 'EU' },
      { department_id: 'D-HR', department: 'HR', region: 'US' },
    ],
  },
  {
    id: 'tbl-employee-salaries',
    name: 'employee_salaries',
    vaultId: 'spc-hr',
    modelIds: ['mdl-compensation'],
    connectionType: 'snowflake',
    classification: 'Confidential',
    tags: ['hr', 'compensation'],
    columns: [
      col('employee_id', 'VARCHAR', 'Internal', ['identifier']),
      col('salary', 'DECIMAL', 'Confidential', ['compensation']),
      col('bonus', 'DECIMAL', 'Confidential', ['compensation']),
      col('currency', 'VARCHAR', 'Internal', ['financial']),
      col('effective_date', 'DATE', 'Internal', ['audit']),
    ],
    rows: [
      { employee_id: 'E001', salary: 180000, bonus: 25000, currency: 'USD', effective_date: '2026-01-01' },
      { employee_id: 'E002', salary: 145000, bonus: 18000, currency: 'USD', effective_date: '2026-01-01' },
      { employee_id: 'E003', salary: 98000, bonus: 8000, currency: 'USD', effective_date: '2026-01-01' },
      { employee_id: 'E004', salary: 112000, bonus: 9000, currency: 'EUR', effective_date: '2026-01-01' },
      { employee_id: 'E005', salary: 155000, bonus: 16000, currency: 'USD', effective_date: '2026-01-01' },
    ],
  },
  {
    id: 'tbl-payroll-records',
    name: 'payroll_records',
    vaultId: 'spc-hr',
    modelIds: ['mdl-compensation'],
    connectionType: 'snowflake',
    classification: 'Restricted',
    tags: ['hr', 'payroll', 'restricted'],
    columns: [
      col('employee_id', 'VARCHAR', 'Internal', ['identifier']),
      col('gross_salary', 'DECIMAL', 'Restricted', ['payroll']),
      col('bank_account', 'VARCHAR', 'Restricted', ['bank', 'restricted']),
      col('payment_date', 'DATE', 'Internal', ['audit']),
    ],
    rows: [
      { employee_id: 'E001', gross_salary: 15000, bank_account: 'US12-0001-8899', payment_date: '2026-09-30' },
      { employee_id: 'E002', gross_salary: 12083, bank_account: 'US12-0002-4411', payment_date: '2026-09-30' },
      { employee_id: 'E003', gross_salary: 8166, bank_account: 'US12-0003-2200', payment_date: '2026-09-30' },
      { employee_id: 'E004', gross_salary: 9333, bank_account: 'EU99-0004-1188', payment_date: '2026-09-30' },
      { employee_id: 'E005', gross_salary: 12916, bank_account: 'US12-0005-7744', payment_date: '2026-09-30' },
    ],
  },
  {
    id: 'tbl-invoices',
    name: 'invoices',
    vaultId: 'spc-finance',
    modelIds: ['mdl-accounts-receivable'],
    connectionType: 'snowflake',
    classification: 'Confidential',
    tags: ['finance', 'ar'],
    columns: [
      col('invoice_id', 'VARCHAR', 'Internal', ['identifier']),
      col('account_id', 'VARCHAR', 'Internal', ['account']),
      col('amount', 'DECIMAL', 'Confidential', ['financial']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('due_date', 'DATE', 'Internal', ['audit']),
    ],
    rows: [
      { invoice_id: 'INV-100', account_id: 'ACC-1', amount: 2400, region: 'US', due_date: '2026-10-01' },
      { invoice_id: 'INV-101', account_id: 'ACC-2', amount: 880, region: 'EU', due_date: '2026-10-08' },
      { invoice_id: 'INV-102', account_id: 'ACC-3', amount: 1560, region: 'US', due_date: '2026-10-15' },
      { invoice_id: 'INV-103', account_id: 'ACC-4', amount: 410, region: 'EU', due_date: '2026-10-22' },
    ],
  },
  {
    id: 'tbl-payments',
    name: 'payments',
    vaultId: 'spc-finance',
    modelIds: ['mdl-accounts-receivable'],
    connectionType: 'bigquery',
    classification: 'PCI',
    tags: ['finance', 'pci'],
    columns: [
      col('payment_id', 'VARCHAR', 'Internal', ['identifier']),
      col('invoice_id', 'VARCHAR', 'Internal', ['invoice']),
      col('amount', 'DECIMAL', 'Confidential', ['financial']),
      col('card_pan', 'VARCHAR', 'PCI', ['pci']),
      col('paid_at', 'TIMESTAMP', 'Internal', ['audit']),
    ],
    rows: [
      { payment_id: 'PAY-1', invoice_id: 'INV-100', amount: 2400, card_pan: '4111111111111111', paid_at: '2026-09-30T16:00:00Z' },
      { payment_id: 'PAY-2', invoice_id: 'INV-101', amount: 880, card_pan: '5555555555554444', paid_at: '2026-10-02T11:20:00Z' },
      { payment_id: 'PAY-3', invoice_id: 'INV-102', amount: 1560, card_pan: '378282246310005', paid_at: '2026-10-04T09:45:00Z' },
    ],
  },
  {
    id: 'tbl-financial-accounts',
    name: 'financial_accounts',
    vaultId: 'spc-finance',
    modelIds: ['mdl-accounts-receivable'],
    connectionType: 'snowflake',
    classification: 'Confidential',
    tags: ['finance'],
    columns: [
      col('account_id', 'VARCHAR', 'Internal', ['identifier']),
      col('account_name', 'VARCHAR', 'Internal', ['name']),
      col('region', 'VARCHAR', 'Internal', ['region']),
      col('balance', 'DECIMAL', 'Confidential', ['financial']),
    ],
    rows: [
      { account_id: 'ACC-1', account_name: 'North America Retail', region: 'US', balance: 184200 },
      { account_id: 'ACC-2', account_name: 'EU Wholesale', region: 'EU', balance: 96200 },
      { account_id: 'ACC-3', account_name: 'US Enterprise', region: 'US', balance: 241500 },
      { account_id: 'ACC-4', account_name: 'EU Partners', region: 'EU', balance: 31800 },
    ],
  },
];

function inferClassification(name: string, pii: boolean, tags: string[]): Classification | undefined {
  if (tags.includes('pci') || /card|pan|cvv/i.test(name)) return 'PCI';
  if (tags.includes('restricted') || /bank|ssn|salary|payroll/i.test(name)) return 'Restricted';
  if (pii || /email|phone|name|address/i.test(name)) return 'PII';
  if (/amount|balance|bonus|currency/i.test(name)) return 'Confidential';
  return 'Internal';
}

function mergeNamedColumns(columns: PolicyColumn[], tableId: string, overrides?: ColumnClassificationMap): PolicyColumn[] {
  return columns.map((column) => {
    if (!overrides) return column;
    const key = classificationKey(tableId, column.name);
    if (!(key in overrides)) return column;
    const next = overrides[key];
    return { ...column, classification: next ?? undefined };
  });
}

function liveAsset(
  table: ModelTable,
  vaultId: string,
  modelId: string,
  connectionType: string,
  overrides?: ColumnClassificationMap,
): PolicyAsset {
  const named = table.sourceId ? undefined : NAMED_ASSETS.find((asset) => asset.id === table.id || asset.name === table.name);
  if (named) {
    const columns = mergeNamedColumns(named.columns, table.id, overrides);
    return {
      ...named,
      id: table.id,
      vaultId,
      modelIds: Array.from(new Set([...named.modelIds, modelId])),
      columns,
      classification: columns.some((column) => column.classification === 'PII') ? 'PII' : named.classification,
    };
  }
  const columns = columnsForTable(table, overrides).map((column) => {
    const forced = Boolean(overrides && classificationKey(table.id, column.name) in overrides);
    return {
      name: column.name,
      type: column.type,
      classification: forced
        ? (column.classification as Classification | undefined)
        : (column.classification as Classification | undefined) ?? inferClassification(column.name, column.pii, column.tags),
      tags: column.tags,
      nullable: column.nullable,
    };
  });
  return {
    id: table.id,
    name: table.name,
    vaultId,
    modelIds: [modelId],
    connectionType,
    sourceId: table.sourceId || undefined,
    path: table.path || undefined,
    classification: columns.some((column) => column.classification === 'PII') ? 'PII' : 'Internal',
    tags: table.name.split('_').slice(0, 2),
    columns,
    rows: Array.from({ length: 5 }, (_, index) =>
      Object.fromEntries(
        columns.slice(0, 6).map((column, col) => [
          column.name,
          col === 0 ? `${column.name}_${index + 1}` : column.type.includes('INT') || column.type === 'NUMBER' || column.type === 'DECIMAL' ? (index + 1) * 10 : `${column.name}_${index + 1}`,
        ]),
      ),
    ),
  };
}

export function buildPolicyCatalog(
  spaces: Space[],
  models: DataModel[],
  tables: ModelTable[],
  overrides?: ColumnClassificationMap,
): {
  vaults: PolicyVault[];
  models: PolicyModel[];
  assets: PolicyAsset[];
} {
  const vaults: PolicyVault[] = spaces.map((space) => ({
    id: space.id,
    name: space.name,
    connections: space.id === 'spc-customer' ? ['Snowflake', 'PostgreSQL', 'BigQuery'] : space.id === 'spc-hr' ? ['PostgreSQL', 'Snowflake'] : space.id === 'spc-finance' ? ['Snowflake', 'BigQuery'] : ['Snowflake'],
    modelIds: models.filter((model) => model.spaceId === space.id).map((model) => model.id),
  }));
  const policyModels: PolicyModel[] = models.map((model) => ({ id: model.id, name: model.name, vaultId: model.spaceId }));
  const assets = tables.map((table) => {
    const model = models.find((item) => item.id === table.modelId);
    const vaultId = model?.spaceId ?? spaces[0]?.id ?? '';
    return liveAsset(
      table,
      vaultId,
      table.modelId,
      table.sourceId.includes('snowflake') ? 'snowflake' : table.sourceId.includes('postgres') ? 'postgresql' : table.sourceId.includes('bq') ? 'bigquery' : 'warehouse',
      overrides,
    );
  });
  const extras = NAMED_ASSETS.filter((asset) => !assets.some((item) => item.id === asset.id || item.name === asset.name));
  if (extras.length) {
    logDataModels('app', 'governance_catalog_named_assets_appended', { count: extras.length });
  }
  return { vaults, models: policyModels, assets: [...assets, ...extras] };
}

export function assetsInScope(assets: PolicyAsset[], scope: PolicyScope): PolicyAsset[] {
  if (scope.level === 'ASSET') return assets.filter((asset) => asset.id === scope.assetId);
  if (scope.level === 'MODEL') return assets.filter((asset) => asset.vaultId === scope.vaultId && asset.modelIds.includes(scope.modelId ?? ''));
  return assets.filter((asset) => asset.vaultId === scope.vaultId);
}

export function modelName(models: PolicyModel[], id?: string): string {
  return models.find((model) => model.id === id)?.name ?? id ?? '';
}

export function vaultName(vaults: PolicyVault[], id: string): string {
  return vaults.find((vault) => vault.id === id)?.name ?? id;
}

export function findUser(id: string): SyntheticUser | undefined {
  return POLICY_USERS.find((user) => user.id === id);
}
